// iCal/ICS Parser for Canvas, Google Calendar, Apple Calendar, Outlook

export interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  location: string;
  startDate: Date;
  endDate: Date;
  allDay: boolean;
  source: 'canvas' | 'google' | 'apple' | 'outlook' | 'file';
  courseCode?: string; // Detected course code like "MBM5524", "COA5593"
}

/**
 * Common course code patterns: letters followed by digits (e.g. MBM5524, COA5593, CS101, PSYCH101)
 */
const COURSE_CODE_REGEX = /\b([A-Za-z]{2,6}\d{3,4})\b/;

/**
 * Detect course code from event title or description
 */
export function detectCourseCode(title: string, description?: string): string | undefined {
  const match = title.match(COURSE_CODE_REGEX);
  if (match) return match[1].toUpperCase();
  if (description) {
    const descMatch = description.match(COURSE_CODE_REGEX);
    if (descMatch) return descMatch[1].toUpperCase();
  }
  return undefined;
}

/**
 * Filter events within a semester date range
 */
export function filterEventsBySemester(events: CalendarEvent[], semesterStart: Date, semesterEnd: Date): CalendarEvent[] {
  return events.filter(event => {
    return event.startDate >= semesterStart && event.startDate <= semesterEnd;
  });
}

/**
 * Group events by detected course code
 */
export function groupEventsByCourse(events: CalendarEvent[]): Map<string, CalendarEvent[]> {
  const groups = new Map<string, CalendarEvent[]>();
  for (const event of events) {
    const code = event.courseCode || 'Uncategorized';
    if (!groups.has(code)) groups.set(code, []);
    groups.get(code)!.push(event);
  }
  return groups;
}

/**
 * Parse iCal/ICS format text into calendar events
 */
export function parseICalendar(icalText: string, source: CalendarEvent['source'] = 'file'): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  
  // Split into individual event blocks
  const eventBlocks = icalText.split('BEGIN:VEVENT');
  
  for (let i = 1; i < eventBlocks.length; i++) {
    const block = eventBlocks[i];
    const endIndex = block.indexOf('END:VEVENT');
    if (endIndex === -1) continue;
    
    const eventText = block.substring(0, endIndex);
    
    try {
      const event = parseEventBlock(eventText, source);
      if (event) {
        events.push(event);
      }
    } catch (error) {
      console.warn('Failed to parse event block:', error);
    }
  }
  
  return events;
}

function parseEventBlock(eventText: string, source: CalendarEvent['source']): CalendarEvent | null {
  const lines = eventText.split('\n').map(line => line.trim()).filter(Boolean);
  
  let uid = '';
  let summary = '';
  let description = '';
  let location = '';
  let dtstart = '';
  let dtend = '';
  
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    
    // Handle multi-line properties (continuation with space)
    while (i + 1 < lines.length && lines[i + 1].startsWith(' ')) {
      line += lines[++i].substring(1);
    }
    
    // Parse property
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;
    
    const property = line.substring(0, colonIndex);
    const value = line.substring(colonIndex + 1);
    
    // Extract base property name (before semicolon parameters)
    const baseProp = property.split(';')[0];
    
    switch (baseProp) {
      case 'UID':
        uid = value;
        break;
      case 'SUMMARY':
        summary = decodeICalText(value);
        break;
      case 'DESCRIPTION':
        description = decodeICalText(value);
        break;
      case 'LOCATION':
        location = decodeICalText(value);
        break;
      case 'DTSTART':
        dtstart = value;
        break;
      case 'DTEND':
        dtend = value;
        break;
    }
  }
  
  if (!uid || !summary || !dtstart) {
    return null;
  }
  
  const startDate = parseICalDate(dtstart);
  const endDate = dtend ? parseICalDate(dtend) : startDate;
  const allDay = dtstart.length === 8; // YYYYMMDD format = all day
  
  return {
    id: uid,
    title: summary,
    description,
    location,
    startDate,
    endDate,
    allDay,
    source,
    courseCode: detectCourseCode(summary, description),
  };
}

function parseICalDate(dateString: string): Date {
  // Remove timezone indicator if present
  dateString = dateString.replace(/Z$/, '').replace(/T.*$/, match => match.replace(/Z$/, ''));
  
  // Format: YYYYMMDD or YYYYMMDDTHHMMSS
  const year = parseInt(dateString.substring(0, 4));
  const month = parseInt(dateString.substring(4, 6)) - 1;
  const day = parseInt(dateString.substring(6, 8));
  
  if (dateString.length === 8) {
    // All-day event
    return new Date(year, month, day);
  }
  
  // Has time component
  const hour = parseInt(dateString.substring(9, 11));
  const minute = parseInt(dateString.substring(11, 13));
  const second = parseInt(dateString.substring(13, 15) || '0');
  
  return new Date(year, month, day, hour, minute, second);
}

function decodeICalText(text: string): string {
  // Unescape iCal special characters
  return text
    .replace(/\\n/g, '\n')
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\\\/g, '\\');
}

/**
 * Fetch calendar from URL (Canvas, Google Calendar feed)
 * Uses a server-side Cloudflare Function proxy to avoid CORS restrictions.
 */
export async function fetchCalendarFromURL(url: string): Promise<string> {
  try {
    const response = await fetch('/api/calendar/fetch', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Server returned ${response.status}`);
    }

    if (!data.icalText) {
      throw new Error('Calendar server returned empty response');
    }

    return data.icalText;
  } catch (error: any) {
    console.error('Calendar fetch error:', error);

    // Provide more helpful error message
    if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
      throw new Error('❌ Cannot connect to the calendar proxy server.\n\n✅ Solution: Download the .ics file from your calendar service and use "Upload .ics File" instead.');
    }

    throw new Error(`Unable to fetch calendar: ${error.message}\n\nTry downloading the .ics file and uploading it instead.`);
  }
}

/**
 * Read calendar from uploaded file
 */
export async function readCalendarFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const text = e.target?.result as string;
      resolve(text);
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsText(file);
  });
}
